import { useCallback, useMemo } from "react";
import { useFormikContext } from "formik";

import { ProductFormData } from "@/components/screens/ProductFormPage/types";
import { useProductFormStateContext } from "@/contexts/product-form-state-context";
import { IProductImage } from "@/interfaces/product-image.interface";

/**
 * Hook dedicado para validação e gerenciamento de imagens em SKUs
 * Implementa cache O(1) para validação de imagens
 *
 * @example
 * ```tsx
 * const { validateSkuImage, validateAndCleanSkuImages } = useSkuImageValidation();
 *
 * if (!validateSkuImage(sku.imagePhisicalAddress)) {
 *   const { cleaned } = validateAndCleanSkuImages();
 *   console.log(`${cleaned} SKU(s) atualizado(s)`);
 * }
 * ```
 */
export const useSkuImageValidation = () => {
  const { savedImages } = useProductFormStateContext();
  const { values, setFieldValue } = useFormikContext<ProductFormData>();

  /**
   * Cache de validação usando Map para busca O(1)
   * Recalcula apenas quando savedImages muda
   */
  const validationCache = useMemo(() => {
    const cache = new Map<string, IProductImage>();
    savedImages.forEach((img) => {
      cache.set(img.phisicalAddress, img);
    });

    return cache;
  }, [savedImages]);

  /**
   * Valida se uma imagem existe (O(1))
   */
  const validateSkuImage = useCallback(
    (imagePhisicalAddress: string): boolean => {
      return validationCache.has(imagePhisicalAddress);
    },
    [validationCache]
  );

  /**
   * Obtém imagem por endereço físico (O(1))
   */
  const getImageByAddress = useCallback(
    (imagePhisicalAddress: string): IProductImage | undefined => {
      return validationCache.get(imagePhisicalAddress);
    },
    [validationCache]
  );

  /**
   * Retorna a imagem padrão do produto
   * Prioridade: imagem principal → primeira imagem → vazio
   */
  const getDefaultImageForSku = useCallback((): string => {
    const mainImage = savedImages.find((img) => img.main);
    if (mainImage) {
      return mainImage.phisicalAddress;
    }

    const firstImage = savedImages[0];
    if (firstImage) {
      return firstImage.phisicalAddress;
    }

    return "";
  }, [savedImages]);

  /**
   * Valida e limpa todas as referências inválidas de SKUs
   * Atualiza o Formik automaticamente
   */
  const validateAndCleanSkuImages = useCallback((): {
    cleaned: number;
    skus: typeof values.skus;
  } => {
    if (!values.skus || values.skus.length === 0) {
      return { cleaned: 0, skus: [] };
    }

    let cleanedCount = 0;
    const updatedSkus = values.skus.map((sku) => {
      if (sku.imagePhisicalAddress && !validateSkuImage(sku.imagePhisicalAddress)) {
        cleanedCount++;
        return {
          ...sku,
          imagePhisicalAddress: "",
          imageUrl: "",
        };
      }
      return sku;
    });

    return { cleaned: cleanedCount, skus: updatedSkus };
  }, [values.skus, validateSkuImage]);

  /**
   * Conta quantos SKUs usam uma imagem específica
   */
  const countSkusUsingImage = useCallback(
    (imagePhisicalAddress: string): number => {
      if (!values.skus) return 0;

      return values.skus.filter((sku) => sku.imagePhisicalAddress === imagePhisicalAddress).length;
    },
    [values.skus]
  );

  /**
   * Obtém lista de SKUs que usam uma imagem específica
   */
  const getSkusUsingImage = useCallback(
    (imagePhisicalAddress: string) => {
      if (!values.skus) return [];

      return values.skus.filter((sku) => sku.imagePhisicalAddress === imagePhisicalAddress);
    },
    [values.skus]
  );

  /**
   * Atualiza SKUs no Formik após limpeza
   */
  const updateSkusInForm = useCallback(
    async (skus: typeof values.skus) => {
      await setFieldValue("skus", skus);
      // Pequeno delay para garantir que Formik processou
      await new Promise((resolve) => setTimeout(resolve, 50));
    },
    [setFieldValue]
  );

  return {
    // Validação
    validateSkuImage,
    getImageByAddress,

    // Limpeza
    validateAndCleanSkuImages,
    updateSkusInForm,

    // Utilitários
    getDefaultImageForSku,
    countSkusUsingImage,
    getSkusUsingImage,

    // Cache (para debug)
    validationCache,
  };
};
